import * as React from 'react';

import {
  Button,
  ConsoleDialog,
  ContentSection,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import {
  variableCreateAtom,
  variableUpdateAtom,
} from '../../_graphql/mutationVariableCrud';
import * as state from '../../_state/variablesList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { VariableRecord } from '../../types/pricing';

const VARIABLE_NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9_]*$/;

interface VariableFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  variable?: VariableRecord;
}

//
// ⚛️ ⚛️ ⚛️ VARIABLE FORM DIALOG ⚛️ ⚛️ ⚛️
//
export const VariableFormDialog = ({
  isOpen,
  onClose,
  onSuccess,
  variable,
}: VariableFormDialogProps) => {
  // Translations
  const t = useTranslations();

  const isEditing = !!variable;

  // States
  const [name, setName] = React.useState(variable?.name ?? '');
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [description, setDescription] = React.useState(
    variable?.description ?? '',
  );
  const [value, setValue] = React.useState<number | null>(
    variable?.value ?? null,
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Data hooks
  const toast = useToast();
  const { refetch } = useAtomValue(state.$remoteVariables);
  const createVariable = useSetAtom(variableCreateAtom({}));
  const updateVariable = useSetAtom(variableUpdateAtom({}));

  // Callbacks
  const handleClose = React.useCallback(() => {
    setName('');
    setNameError(null);
    setDescription('');
    setValue(null);
    onClose();
  }, [onClose]);

  const handleNameChange = React.useCallback(
    (val: string) => {
      setName(val);
      if (val.trim() && !VARIABLE_NAME_REGEX.test(val.trim())) {
        setNameError(t.pricing.variables.validation.nameFormat);
      } else {
        setNameError(null);
      }
    },
    [t],
  );

  const handleValueChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setValue(raw === '' ? null : Number(raw));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    if (!name.trim()) {
      toast.error(t.pricing.variables.validation.nameRequired);
      return;
    }
    if (!VARIABLE_NAME_REGEX.test(name.trim())) {
      toast.error(t.pricing.variables.validation.nameFormat);
      return;
    }

    try {
      setIsSubmitting(true);
      if (isEditing && variable) {
        const result = await updateVariable({
          id: variable.id,
          name: name.trim(),
          description: description.trim() || null,
        });
        const errors = result?.pricingVariableUpdate?.validationErrors ?? [];
        if (errors.length > 0) {
          toast.warning(errors.map((e) => e.formulaType ?? '').join('; '));
        }
        toast.success(t.pricing.variables.toast.updated);
      } else {
        await createVariable({
          name: name.trim(),
          description: description.trim() || null,
          value,
        });
        toast.success(t.pricing.variables.toast.created);
      }
      refetch?.();
      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      const toastMessage = isEditing
        ? t.pricing.variables.toast.updateError(errorMessage)
        : t.pricing.variables.toast.createError(errorMessage);
      toast.error(toastMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    createVariable,
    updateVariable,
    name,
    description,
    value,
    isEditing,
    variable,
    toast,
    refetch,
    onSuccess,
    handleClose,
    t,
  ]);

  // Effects
  React.useEffect(() => {
    if (isOpen) {
      setName(variable?.name ?? '');
      setNameError(null);
      setDescription(variable?.description ?? '');
      setValue(variable?.value ?? null);
    }
  }, [isOpen, variable]);

  const canSubmit = Boolean(name.trim()) && !nameError && !isSubmitting;

  let submitLabel = isEditing
    ? t.pricing.variables.edit.submitButton
    : t.pricing.variables.create.submitButton;
  if (isSubmitting) submitLabel = t.common.loading.creating;

  return (
    <ConsoleDialog
      className="@VariableFormDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={
        isEditing
          ? t.pricing.variables.edit.title
          : t.pricing.variables.create.title
      }
      contentClassName="px-0"
      content={
        <div className="space-y-6">
          <ContentSection
            variant="panel"
            hideDivider
            title={t.pricing.variables.create.sections.details}
          >
            <div className="grid gap-4">
              <TextField
                label={t.pricing.variables.create.fields.name}
                value={name}
                onChange={handleNameChange}
                placeholder={t.pricing.variables.create.placeholders.name}
                isRequired
                errorMessage={nameError ?? undefined}
                isInvalid={!!nameError}
              />
              {!isEditing && (
                <div className="grid gap-1.5">
                  <label className="text-sm font-medium">
                    {t.pricing.variables.create.fields.value}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full rounded border border-neutral/20 bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                    value={value ?? ''}
                    onChange={handleValueChange}
                  />
                </div>
              )}
              <TextField
                label={t.pricing.variables.create.fields.description}
                value={description}
                onChange={setDescription}
                placeholder={
                  t.pricing.variables.create.placeholders.description
                }
              />
            </div>
          </ContentSection>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitLabel}
          </Button>
        </div>
      }
    />
  );
};
